import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import store from './../../store';
import { getErrorNotification } from "../../actions/toast";
import { filterData } from '../../actions/filterData';
import { countDate } from '../../actions/date';
import { getOffices } from '../../actions/offices';
import BankProductsService from '../../services/bank-products.service';
import BankCartService from '../../services/bank-cart.service';
import BankTransactionsService from '../../services/bank-transactions.service';
import { useTranslation } from 'react-i18next';
import usePhoneCheck from '../../hooks/usePhoneCheck';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import BankMobileBody from '../../components/ComponentTable/Table/BankMobileBody';
import BankTableBody from '../../components/ComponentTable/Table/BankTableBody';
import BankTableHead from '../../components/ComponentTable/Table/BankTableHead';
import BankTableToolbar from '../../components/ComponentTable/Table/BankTableToolbar';
import {
    Stack,
    Box,
    Table,
    TableContainer,
    Paper,
    CircularProgress,
    TextField,
    FormControl,
    NativeSelect,
} from '@mui/material';
import { toast } from 'react-toastify';


const headCells = [
    {
        id: 'checkbox',
        numeric: false,
        disablePadding: false,
        label: '',
        type: 'checkbox',
    },
    {
        id: 'img',
        numeric: false,
        disablePadding: false,
        label: 'storefrontComponent.image',
        align: 'left',
        type: 'img',
    },
    {
        id: 'title',
        numeric: false,
        disablePadding: false,
        label: 'storefrontComponent.productName',
        align: 'left',
        type: 'string',
    },
    {
        id: 'product_type',
        numeric: false,
        disablePadding: false,
        label: 'modalAddProduct.productType',
        align: 'left',
        type: 'product_type',
    },
    {
        id: 'inClasses',
        numeric: false,
        disablePadding: false,
        label: 'storefrontComponent.inClasses',
        align: 'left',
        type: 'string',
    },
    {
        id: 'inMyClass',
        numeric: false,
        disablePadding: false,
        label: 'storefrontComponent.inMyClass',
        align: 'left',
        type: 'stringWithTooltip',
    },
    {
        id: 'amount',
        numeric: true,
        disablePadding: false,
        label: 'storefrontComponent.quantity',
        align: 'left',
        type: 'amount',
    },
    {
        id: 'price',
        numeric: false,
        disablePadding: false,
        label: 'storefrontComponent.price',
        align: 'left',
        type: 'string',
    },
    {
        id: 'vip',
        numeric: false,
        disablePadding: false,
        label: 'VIP',
        align: 'left',
        type: 'vip',
    },
    {
        id: 'quantity',
        numeric: true,
        disablePadding: false,
        label: 'storefrontComponent.quantityToOrder',
        type: 'integerInput',
    },
    {
        id: 'selectOffice',
        numeric: true,
        disablePadding: false,
        label: 'orderedProducts.office',
        type: 'selectOffice',
    },
    {
        id: 'additionalInfo',
        numeric: false,
        disablePadding: false,
        label: 'storefrontComponent.additionalInfo',
        type: 'additionalInfo',
      },
];

export default function StorefrontComponent() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [initialRows, setInitialRows] = useState([]);
    const [filters, setFilters] = useState({ title: '' });
    const [selected, setSelected] = useState([]);
    const [unapprovedFranchiseOrders, setUnapprovedFranchiseOrders] = useState({});

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const dispatch = useDispatch();
    const roleId = useSelector((state) => state.user.info.role_id);
    const offices = useSelector((state) => state.offices.list);
    const { emulation } = store.getState().user;
    const sortedRows = [...rows].sort((a, b) => a.promo - b.promo);
    const { t, i18n } = useTranslation();
    const mobile = usePhoneCheck();
    const langMap = { ru: 0, ua: 1, en: 2, ro: 6, by: 0, ee: 2 };

    const getUnapprovedFranchiseOrders = async () => {
        const response = await BankTransactionsService.getPendingTeacherOrders();
        const tooltipMap = {};
        response?.forEach(item => {
            const { product_id, date, qty } = item;

            if (!tooltipMap[product_id]) {
                tooltipMap[product_id] = [];
            }

            tooltipMap[product_id].push(`${countDate(date, true)} | ${qty} ${t('editProductQuantity.toastError_2')}`);
        });

        setUnapprovedFranchiseOrders(tooltipMap);
    };

    const getActiveProducts = async () => {
        const res = await BankProductsService.GetActiveProducts();
        const products = Array.isArray(res)
            ? res.map((item) => ({
                rowId: item.id,
                title: item.title,
                amount: item.amount || 0,
                price: item.price,
                vip: item.vip || null,
                img: item.img,
                promo: item.promo,
                detail: item.detail,
                inClasses: item.amount_other,
                inMyClass: item.amount_my,
                quantity: 1,
                product_type: item.product_type,
                selectOffice: "",
                additionalInfo: "",
            }))
            : [];

        setRows(products);
        setInitialRows(products);
        setLoading(false);
        getUnapprovedFranchiseOrders();
    };

    useEffect(() => {
        getActiveProducts();
        dispatch(getOffices(false, { size: 9999 }));
    }, []);

    const updateRowField = (id, field, value) => {
        setRows(prevRows =>
            prevRows.map(row =>
                row.rowId === id
                    ? {
                        ...row,
                        [field]:
                            field === "quantity"
                                ? parseFloat(value) || 0
                                : value,
                    }
                    : row
            )
        );
    };

    const handleQuantityChange = (id, value) => {
        updateRowField(id, "quantity", value);
    };
    
    const handleSelectOfficeChange = (id, value) => {
        updateRowField(id, 'selectOffice', value); 
    };

    const renderAdditionalInfoInput = (rowId) => {
        const row = rows.find(r => r.rowId === rowId);
        if (!row) return null;

        const { additionalInfo, selectOffice } = row;

        const office = offices?.find(o => String(o.geo_id) === String(selectOffice));
        const title = office ? (office.title || '').split('|||')[langMap[i18n.language]] || '' : '';
        const isOnlineOffice = title.toLowerCase().includes('online') || title.toLowerCase().includes('онлайн');

        const hasError = isOnlineOffice && !(additionalInfo || '').trim();

        return (
            <TextField
                id={`additionalInfo-${rowId}`}
                value={additionalInfo || ""}
                variant="standard"
                onChange={(e) => updateRowField(rowId, "additionalInfo", e.target.value)}
                error={hasError}
                helperText={hasError ? t('storefrontComponent.helperText') : ""}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            />
        );
    };

    const renderOfficeSelect = (rowId, selectedOfficeId, onChange) => {
        const isRowSelected = isSelected(rowId);
        const hasError = isRowSelected && !selectedOfficeId;

        return (
            <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 150 }}
                error={hasError}
            >
                <NativeSelect
                    value={selectedOfficeId || ''}
                    onChange={(e) => onChange(rowId, e.target.value)}
                    style={{
                        borderBottom: hasError
                            ? '2px solid red'
                            : '2px solid rgba(0,0,0,0.42)',
                        padding: '2px 0'
                    }}
                >
                    <option value="">{t('modals.choose_office')}</option>
                    {offices?.map((item, key) => (
                        <option key={key} value={item.geo_id} disabled={item.disabled}>
                            {item.title.split('|||')[langMap[i18n.language]]}
                        </option>
                    ))}
                </NativeSelect>

                {hasError && (
                    <p style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                        {t('modals.choose_office')}
                    </p>
                )}
            </FormControl>
        );
    };

    const handleSelectAllClick = event => {
        if (event.target.checked) {
            const newSelected = rows.map(n => n.rowId);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
    };

    const isSelected = id => selected.indexOf(id) !== -1;

    async function handleSuccessClick() {
        const selectedRows = rows.filter((row) => isSelected(row.rowId));
        const hasEmptyQuantity = selectedRows.some((row) => !row.quantity);
        const hasEmptyOffice = selectedRows.some((row) => !row.selectOffice);
        const hasError = selectedRows.some(row => {
            const office = offices.find(o => String(o.geo_id) === String(row.selectOffice));
            if (!office) return false;
            const title = (office.title || '').split('|||')[langMap[i18n.language]] || '';
            const isOnlineOffice = title.toLowerCase().includes('online') || title.toLowerCase().includes('онлайн');
            return isOnlineOffice && !(row.additionalInfo || '').trim();
        });
        
        let cartIds = [];

        if (hasEmptyQuantity) {
            getErrorNotification(t('storefrontComponent.quantityToMuch'));
            return;
        };

        if (hasEmptyOffice) {
            getErrorNotification(t('storefrontComponent.officeError'));
            return;
        };
        
        if (hasError) {
            getErrorNotification(t('storefrontComponent.onlineOfficeError'));
            return;
        };

        try {
            await Promise.all(
                selectedRows.map(async item => {
                    const response = await BankCartService.AddItemToCart({
                        productId: Number(item.rowId),
                        quantity: Number(item.quantity),
                        office_geo_id: item.selectOffice,
                        additional_info: item.additionalInfo || "", 
                    });
                    if (response && response[0]?.cart) {
                        cartIds.push(Number(response[0].cart));
                    }
                })
            );
        } catch (error) {
            console.log(error);
        }

        if (cartIds.length) {
            try {
                await BankCartService.ConfirmProductsByIds(
                    {
                        cartIds: cartIds,
                    },
                    true
                );
                await getActiveProducts();
                setSelected([]);
            } catch (error) {
                console.log(error);
            }
        }
    }

    function handleDeleteClick() {
        setSelected([]);
    }

    const handleFilterChange = (key, value) => {
        if (loading) return;
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        setRows(filterData(initialRows, newFilters));
    };

    const filterConfigs = [{ label: t('storefrontComponent.productName'), key: 'title' }];

    if (loading) {
        return (
            <Paper elevation={0}>
                <Box m={2} p={2}>
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    if (!loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <Stack
                        direction={mobile ? 'column' : 'row'}
                        justifyContent="flex-start"
                        spacing={2}
                        style={{ marginTop: '8px', padding: '14px' }}
                    >
                        {filterConfigs.map(({ label, key, transform }) => (
                            <CustomAutocomplete
                                key={key}
                                label={label}
                                options={[
                                    ...new Set(rows.map(row => (transform ? transform(row[key]) : row[key]) || '')),
                                ]}
                                inputValue={filters[key] || ''}
                                onChange={(e, newValue) => handleFilterChange(key, newValue || '')}
                                onInputChange={(e, newInputValue) =>
                                    setFilters(prev => ({
                                        ...prev,
                                        [key]: newInputValue || '',
                                    }))
                                }
                            />
                        ))}
                    </Stack>
                    <BankTableToolbar
                        title={t('orderedProducts.selected')}
                        titleTooltipSuccess={t('modalCart.buyProduct')}
                        titleTooltipDelete={t('modalCart.removeProductFromCart')}
                        numSelected={selected.length}
                        onSuccessClick={handleSuccessClick}
                        onDeleteClick={handleDeleteClick}
                        selectedUserIds={selected}
                        rows={rows}
                    />
                    {mobile ? (
                        <BankMobileBody
                            rows={sortedRows}
                            headCells={headCells}
                            roleId={roleId}
                            emulation={emulation}
                            mobile={mobile}
                            isSelected={isSelected}
                            handleQuantityChange={handleQuantityChange}
                            handleSelectAllClick={handleSelectAllClick}
                            handleClick={handleClick}
                            selected={selected}
                            tooltipMap={unapprovedFranchiseOrders}
                            renderAdditionalInfoInput={renderAdditionalInfoInput}
                            renderOfficeSelect={renderOfficeSelect}
                            handleSelectOfficeChange={handleSelectOfficeChange}
                        />
                    ) : (
                        <TableContainer style={{ height: '69vh' }}>
                            <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                                <BankTableHead
                                    headCells={headCells}
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                    onSelectAllClick={handleSelectAllClick}
                                    selected={selected}
                                    rows={sortedRows}
                                />
                                <BankTableBody
                                    rows={sortedRows}
                                    headCells={headCells}
                                    roleId={roleId}
                                    emulation={emulation}
                                    order={order}
                                    orderBy={orderBy}
                                    isSelected={isSelected}
                                    handleQuantityChange={handleQuantityChange}
                                    handleClick={handleClick}
                                    tooltipMap={unapprovedFranchiseOrders}
                                    renderAdditionalInfoInput={renderAdditionalInfoInput}
                                    renderOfficeSelect={renderOfficeSelect}
                                    handleSelectOfficeChange={handleSelectOfficeChange}
                                />
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Box>
        );
    }
}
